<template>
  <CloseHeader :title="`${t('setting')}`" />

  <div class="app-container">
    <div class="my-info">
      <div class="name-item">
        <p>{{ userInfo?.nickname }}</p>
        <span class="badge primary-bg" v-if="userInfo?.userType === 'HOST'">{{ subscriptionStore.userPlan }}</span>
      </div>

      <button class="info-modify-btn" @click="goToPage('app-user-info')">
        <span>{{ t('manage_my_account') }}</span>
        <div class="mail-info">
          <div class="social-img" v-if="loginType">
            <img :class="loginType.class" :src="loginType.src" :alt="loginType.alt" />
          </div>

          <p class="id-name">{{ userInfo?.loginId }}</p>
          <IconArrow class="icon aw-icon" />
        </div>
      </button>
    </div>

    <div class="import-menu">
      <button class="item-btn" v-if="userInfo?.userType === 'HOST'" @click="goToPage('app-credit')">
        <img :src="'/images/mobile/icon/credit-icon.svg'" alt="" />
        <span>{{ t('credit_status') }}</span>
      </button>
      <button class="item-btn" @click="goToPage('app-chat-list')">
        <img :src="'/images/mobile/icon/interpretation-list-icon.svg'" alt="" />
        <span>{{ t('my_interpreter') }}</span>
      </button>
      <button class="item-btn" @click="goToPage('app-chats-detail')">
        <img :src="'/images/mobile/icon/history-icon.svg'" alt="" />
        <span>{{ t('interpretation_history') }}</span>
      </button>
    </div>

    <div class="link-box" v-if="subscriptionStore.userPlan !== 'Free' && userInfo?.userType === 'HOST'">
      <!-- TODO: 인앱 결제시, 해당 if 삭제 -->
      <p>{{ t('setting') }}</p>
      <ul class="inr-list">
        <!-- TODO: 인앱 결제시, 구현 예정
        <li>
          <button>
            <span>{{ t('plan_setting') }}</span>
            <IconArrow class="icon aw-icon" />
          </button>
        </li>
        <li>
          <button>
            <span>카드 관리</span>
            <IconArrow class="icon aw-icon" />
          </button>
        </li> 
        -->
        <li>
          <button @click="goToPage('native-team')">
            <span>{{ t('label_user_management') }}</span>
            <IconArrow class="icon aw-icon" />
          </button>
        </li>
      </ul>
    </div>

    <div class="link-box">
      <p>{{ t('label_guide') }}</p>
      <ul class="inr-list">
        <li>
          <button @click="goToPage('app-customer-faq')">
            <span>{{ t('support_center') }}</span>
            <IconArrow class="icon aw-icon" />
          </button>
        </li>
        <li>
          <button @click="openPolicyPopup('policy-service')">
            <span>{{ t('policy_service') }}</span>
            <IconArrow class="icon aw-icon" />
          </button>
        </li>
        <li>
          <button @click="openPolicyPopup('policy-privacy')">
            <span>{{ t('policy_privacy') }}</span>
            <IconArrow class="icon aw-icon" />
          </button>
        </li>
        <li>
          <button @click="openPolicyPopup('policy-info')">
            <span>{{ t('policy_info') }}</span>
            <IconArrow class="icon aw-icon" />
          </button>
        </li>
        <li>
          <button @click="openPolicyPopup('policy-refund')">
            <span>{{ t('policy_payment') }}</span>
            <IconArrow class="icon aw-icon" />
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import CloseHeader from '@/components/domains/mobile/common/CloseHeader.vue';
import BottomSheet from '@/components/domains/mobile/common/PopupBottomSheet.vue';
import PopupPolicyInfo from '@/components/domains/mobile/popup/PopupPolicyInfo.vue';
import PopupPolicyPrivacy from '@/components/domains/mobile/popup/PopupPolicyPrivacy.vue';
import PopupPolicyRefund from '@/components/domains/mobile/popup/PopupPolicyRefund.vue';
import PopupPolicyService from '@/components/domains/mobile/popup/PopupPolicyService.vue';
import IconArrow from '@/components/jh-app/icons/IconArrow.vue';
import { usePopupStore } from '@/stores/popup';
import { useSubscriptionStore } from '@/stores/subscription';
import { useUserStore } from '@/stores/user';
import { PolicyNameType } from '@/types/components';

const { t } = useI18n();
const router = useRouter();

const userStore = useUserStore();
const subscriptionStore = useSubscriptionStore();
const popupStore = usePopupStore();

const userInfo = ref({
  userId: '',
  loginId: '',
  teamId: '',
  email: '',
  userType: '',
  nickname: '',
  languageCode: 'ko',
  registerType: 'NORMAL',
  countryCode: '',
  partnerId: 0,
  initPassword: false,
  storeAgreement: false,
  isTester: false,
});

const loginType = computed(() => LOGIN_TYPE_ICONS[userInfo.value.registerType] ?? null);

const LOGIN_TYPE_ICONS = {
  THIRD_PARTY_GOOGLE: { src: '/images/mobile/icon/google-logo.svg', alt: 'google' },
  THIRD_PARTY_META: { src: '/images/mobile/icon/facebook-color.svg', alt: 'facebook', class: 'facebook' },
  THIRD_PARTY_APPLE: { src: '/images/mobile/icon/apple-color.svg', alt: 'apple' },
};

const goToPage = (name: string) => {
  if (name === 'native-team') {
    if (userInfo.value.teamId) {
      router.push({ name: 'app-team-manage' });
    } else {
      router.push({ name: 'app-team-setup' });
    }
    return;
  }

  router.push({ name });
};

const openPolicyPopup = (name: PolicyNameType) => {
  const policyComponent = {
    'policy-service': PopupPolicyService,
    'policy-privacy': PopupPolicyPrivacy,
    'policy-refund': PopupPolicyRefund,
    'policy-info': PopupPolicyInfo,
  }[name];

  popupStore.open(BottomSheet, {
    contentSlot: h(policyComponent),
    showCloseButton: true,
  });
};

onMounted(async () => {
  await userStore.fetchUserInfo();
  userInfo.value = userStore.userInfo;
});
</script>

<style scoped lang="scss">
.my-info {
  padding: 1.5rem 0;
  .name-item {
    display: flex;
    gap: $size-xs;
    margin-bottom: 0.5rem;
    align-items: center;
  }
  .info-modify-btn {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    gap: $size-s;
    > span {
      font-size: $font-size-psmall;
      font-weight: 400;
      color: $color-gray-200;
      white-space: nowrap;
    }
    .mail-info {
      display: flex;
      gap: $size-xs;
      align-items: center;
      .social-img {
        min-width: 1.5rem;
        max-width: 1.5rem;
        min-height: 1.5rem;
        max-height: 1.5rem;
        border: 1px solid $color-gray-40;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        img {
          width: 0.75rem;
          height: 0.75rem;
          object-fit: contain;
          &.facebook {
            width: 0.875rem;
            height: 0.875rem;
          }
        }
      }
      span {
        font-weight: 400;
        font-size: $font-size-psmall;
      }
    }
    .id-name {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2; /* 보여줄 줄 수 (2줄) */
      overflow: hidden;
      text-overflow: ellipsis; /* ... 처리를 위해 */
      word-break: break-all; /* 영어/숫자가 섞여있을 때 단어 중간에서도 줄바꿈 */
      text-align: left;
    }
  }
}
.link-box {
  padding: 1.5rem 1.25rem;
  background: $gray10;
  margin-top: $size-l;
  border-radius: $size-m;
  > p {
    font-size: $font-size-psmall;
    font-weight: 600;
  }
  .inr-list {
    margin-top: 1rem;
    li {
      button {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.5rem 0;
        width: 100%;
        height: 2rem;
        span {
          font-size: $font-size-psmall;
          font-weight: 400;
        }
      }
    }
  }
}
.import-menu {
  padding: 1.25rem 0;
  background: $gray10;
  display: flex;
  justify-content: space-between;
  border-radius: $size-m;
  .item-btn {
    display: flex;
    flex-direction: column;
    gap: $size-xs;
    align-items: center;
    flex: 1;
    padding: 0;
    position: relative;
    border-right: 1px solid $color-gray-40;
    // &::after {
    //   content: '';
    //   display: block;
    //   position: absolute;
    //   height: 12px;
    //   width: 1px;
    //   background: $color-gray-40;
    //   right: 0;
    //   top: 50%;
    //   transform: translateY(-50%);
    // }
    &:last-child {
      border-right: 0;
    }
    span {
      font-size: $font-size-psmall;
      font-weight: 400;
    }
  }
}

a {
  text-decoration: none;
  color: initial;
}
.aw-icon {
  position: relative;
  transform: rotate(90deg);
  top: 1px;
  color: $color-gray-80;
}
</style>
